import React, { useEffect, useRef } from 'react';

interface Orb {
  x: number;
  y: number;
  radius: number;
  dx: number;
  dy: number;
  color: string;
}

export const AnimatedBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size with device pixel ratio for smooth rendering
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();

      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      // Scale canvas context to account for extra pixels
      ctx.scale(dpr, dpr);

      // Set display size
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create floating color orbs
    const orbs: Orb[] = [
      { x: window.innerWidth * 0.2, y: window.innerHeight * 0.3, radius: 300, dx: 0.4, dy: 0.3, color: 'rgba(82, 96, 255, 0.8)' },
      { x: window.innerWidth * 0.8, y: window.innerHeight * 0.5, radius: 350, dx: -0.3, dy: 0.4, color: 'rgba(138, 43, 226, 0.7)' },
      { x: window.innerWidth * 0.5, y: window.innerHeight * 0.7, radius: 280, dx: 0.35, dy: -0.35, color: 'rgba(0, 191, 255, 0.75)' },
      { x: window.innerWidth * 0.6, y: window.innerHeight * 0.2, radius: 250, dx: -0.25, dy: 0.3, color: 'rgba(255, 0, 128, 0.6)' },
      { x: window.innerWidth * 0.3, y: window.innerHeight * 0.8, radius: 320, dx: 0.3, dy: -0.25, color: 'rgba(30, 50, 180, 0.65)' },
    ];

    let animationFrameId: number;

    const animate = () => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      orbs.forEach((orb) => {
        // Update position
        orb.x += orb.dx;
        orb.y += orb.dy;

        // Bounce off edges
        if (orb.x + orb.radius > window.innerWidth || orb.x - orb.radius < 0) {
          orb.dx = -orb.dx;
        }
        if (orb.y + orb.radius > window.innerHeight || orb.y - orb.radius < 0) {
          orb.dy = -orb.dy;
        }

        // Draw gradient orb
        const gradient = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.radius);
        gradient.addColorStop(0, orb.color);
        gradient.addColorStop(0.5, orb.color.replace(/[\d.]+\)$/, '0.3)'));
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(orb.x, orb.y, orb.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <>
      {/* Animated Canvas Background with Blur */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 0, filter: 'blur(120px)' }}
      />

      {/* Radial Gradient Overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 1,
          background: 'radial-gradient(circle, transparent 0%, rgba(5, 5, 5, 0.3) 70%, rgba(5, 5, 5, 0.7) 100%)'
        }}
      />
    </>
  );
};
